import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

export type KoobTableToolbarProps = {
  /** A search field (KoobSearchBar, an app's URL-bound search). */
  search?: React.ReactNode;
  /** Filter controls beside the search (selects, segmented, dates). */
  filters?: React.ReactNode;
  /** Buttons at the right (export, locale, create). */
  actions?: React.ReactNode;
};

const hasContent = ({ search, filters, actions }: KoobTableToolbarProps) =>
  Boolean(search || filters || actions);

/** The bar on top of a table, inside its card: search, filters, actions.
 * KoobDataTable draws it from its own props or from the KoobListPageLayout
 * around it; give it to KoobTable's `headerBar` for a hand-built table. */
export default function KoobTableToolbar(props: KoobTableToolbarProps) {
  const { search, filters, actions } = props;
  if (!hasContent(props)) return null;
  return (
    <div className="flex flex-wrap items-end gap-3 border-0 border-b border-solid border-k-gray px-5 py-4">
      {search && (
        <div className="w-full sm:w-auto sm:min-w-48 sm:max-w-72 sm:flex-1">
          {search}
        </div>
      )}
      {filters && (
        <div className="flex flex-wrap items-end gap-3">{filters}</div>
      )}
      {actions && (
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {actions}
        </div>
      )}
    </div>
  );
}

// A list page's toolbar goes to the first table inside it. Until one claims
// it (a page showing cards, a timeline or a loader in place of its table),
// the page draws a card of its own: the toolbar on top, the content inside,
// as a table would.
type Slot = {
  toolbar: KoobTableToolbarProps;
  claim: (id: object) => boolean;
  release: (id: object) => void;
};

const ToolbarSlot = createContext<Slot | null>(null);

/** Holds a list page's toolbar for the table inside, and frames the content
 * with it while no table has claimed it. */
export function KoobTableToolbarProvider({
  toolbar,
  footer,
  embedded = false,
  children,
}: {
  toolbar: KoobTableToolbarProps;
  /** Flush with the card's bottom edge: an embedded KoobPagination. */
  footer?: React.ReactNode;
  /** Inside another card: keep the toolbar and padding, drop the frame. */
  embedded?: boolean;
  children: React.ReactNode;
}) {
  const owner = useRef<object | null>(null);
  const [claimed, setClaimed] = useState(false);
  const slot: Slot = {
    toolbar,
    claim: (id) => {
      if (owner.current && owner.current !== id) return false;
      owner.current = id;
      setClaimed(true);
      return true;
    },
    release: (id) => {
      if (owner.current !== id) return;
      owner.current = null;
      setClaimed(false);
    },
  };
  const framed = !claimed && hasContent(toolbar);
  // The same elements framed or not (`contents` drops their box), so a table
  // claiming the toolbar never remounts the page's content.
  return (
    <ToolbarSlot.Provider value={slot}>
      <div
        className={
          !framed
            ? 'contents'
            : embedded
              ? ''
              : 'rounded-2xl border border-solid border-k-gray bg-card'
        }
      >
        {framed && (
          // Stays in view while the content scrolls under it, as a table's.
          <div className="sticky top-[var(--koob-sticky-top,0px)] z-raised rounded-t-2xl bg-card">
            <KoobTableToolbar {...toolbar} />
          </div>
        )}
        <div className={framed ? 'p-5' : 'contents'}>{children}</div>
        {footer}
      </div>
    </ToolbarSlot.Provider>
  );
}

/** For a table: the toolbar of the page around it, if this table is the
 * first to ask. Layout effect, so the page's own copy never paints. */
export function usePageToolbar(): KoobTableToolbarProps | null {
  const slot = useContext(ToolbarSlot);
  const id = useRef({}).current;
  const [mine, setMine] = useState(false);
  const claim = slot?.claim;
  const release = slot?.release;
  useLayoutEffect(() => {
    if (!claim || !release) return undefined;
    setMine(claim(id));
    return () => release(id);
    // claim/release change identity each render; the owner ref keeps them
    // consistent, so claiming once on mount is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(slot)]);
  return mine && slot ? slot.toolbar : null;
}
